import { useState, useEffect, useRef } from 'react';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  DocumentArrowDownIcon
} from '@heroicons/react/24/outline';
import { utils as XLSXUtils, write as XLSXWrite } from 'xlsx';
import JsPDF from 'jspdf';
import 'jspdf-autotable';
import { useTranslation } from 'react-i18next';
import 'styles/ActivitiesManagement.css';
import { getFormattedMissingFieldsMessage } from 'utils';
import { useAuth } from 'auth/AuthContext';
import { makeRequest } from 'services/request.service';
import { showAlert } from 'services/alerts.service';
import EditActivityModal from 'components/modals/EditActivityModal';
import AddActivityModal from 'components/modals/AddActivityModal';
import ButtonComponent from 'components/common/ButtonComponent';
import TableComponent from 'components/common/TableComponent';
import ActionCardComponent from 'components/common/ActionCardComponent';
import SectionIntro from 'components/common/SectionIntro';

const ActivitiesManagement = () => {
  const { t } = useTranslation();
  const hasFetched = useRef(false);
  const { user } = useAuth();
  const [state, setState] = useState({
    activities: [],
    loading: true,
    isEditActivityModalOpen: false,
    isAddActivityModalOpen: false,
    selectedActivity: null,
    columnName: '',
    orderBy: 'ASC',
    filter: '',
    page: 1,
    limit: 10,
    searchTerm: ''
  });

  const { activities } = state;

  const getUrl = () => {
    const params = new URLSearchParams({
      filter: state.filter || ''
    });

    if (state.page) params.set('page', state.page);
    if (state.limit) params.set('limit', state.limit);
    if (state.columnName) params.set('columnName', state.columnName);
    if (state.orderBy) params.set('orderBy', state.orderBy);

    return `/management/activities/${user.farmId}?${params.toString()}`;
  };

  const fetchActivities = async () => {
    try {
      const response = await makeRequest({
        method: 'get',
        url: getUrl(),
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
      });
      if (response.status === 200) {
        setState(s => ({ ...s, activities: response.data, loading: false }));
      }
    } catch (error) {
      setState(s => ({
        ...s,
        error: error.message,
        selectedActivity: null
      }));
    }
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    fetchActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.filter, state.page, state.limit, state.columnName, state.orderBy]);

  const handleExportExcel = () => {
    try {
      const dataToExport = activities.map(activity => ({
        ID: activity.activityId,
        Name: activity.activityName,
        Status: activity.isActive ? t('common.active') : t('common.inactive'),
        'All Devices': activity.allDevices,
        Shared: activity.shared
      }));

      const worksheet = XLSXUtils.json_to_sheet(dataToExport);
      const workbook = XLSXUtils.book_new();
      XLSXUtils.book_append_sheet(workbook, worksheet, 'Activities');

      // Ajustar el ancho de las columnas
      const maxWidth = dataToExport.reduce((w, r) => Math.max(w, r.Name.length), 10);
      worksheet['!cols'] = [
        { wch: maxWidth }, // Name
        { wch: 15 }, // Status
        { wch: 15 }, // All Devices
        { wch: 15 } // Shared
      ];

      const excelBuffer = XLSXWrite(workbook, {
        bookType: 'xlsx',
        type: 'array'
      });

      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `activities_${new Date().toISOString().split('T')[0]}.xlsx`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      showAlert({
        text: 'Excel file downloaded successfully',
        icon: 'success'
      });
    } catch (error) {
      showAlert({
        text: 'Failed to export Excel file',
        icon: 'error'
      });
    }
  };

  const handleExportPDF = () => {
    try {
      const doc = new JsPDF();

      doc.setFontSize(16);
      doc.text('Activities Report', 14, 15);
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 25);

      const tableColumns = ['Name', 'Status', 'All Devices', 'Shared'];

      const tableData = state.activities.map(activity => [
        activity?.activityName || activity?.activity_name || activity?.name || 'N/A',
        activity?.isActive || activity?.is_active ? 'Active' : 'Inactive',
        activity?.allDevices || activity?.all_devices ? 'Yes' : 'No',
        activity?.shared ? 'Yes' : 'No'
      ]);

      doc.autoTable({
        startY: 30,
        head: [tableColumns],
        body: tableData,
        theme: 'grid',
        styles: {
          fontSize: 8,
          cellPadding: 2,
          overflow: 'linebreak'
        },
        headStyles: {
          fillColor: [67, 160, 71],
          textColor: 255,
          fontSize: 9,
          fontStyle: 'bold'
        },
        columnStyles: {
          0: { cellWidth: 'auto' }, // Name
          1: { cellWidth: 30 }, // Status
          2: { cellWidth: 30 }, // All Devices
          3: { cellWidth: 30 } // Shared
        }
      });

      doc.save(`activities_${new Date().toISOString().split('T')[0]}.pdf`);

      showAlert({
        text: 'PDF file downloaded successfully',
        icon: 'success'
      });
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      showAlert({
        text: 'Failed to export PDF file',
        icon: 'error'
      });
    }
  };

  const handleEdit = activity => {
    setState(s => ({ ...s, selectedActivity: activity, isEditActivityModalOpen: true }));
  };

  const handleDelete = async activity => {
    try {
      await makeRequest({
        method: 'delete',
        url: `/management/activities/${activity.activityId}`,
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` },
        alertResult: true
      });

      fetchActivities();
    } catch (error) {
      const serverMessage = error?.response?.data?.message || error.message;

      const isConstraintError =
        serverMessage.includes('violates foreign key constraint') ||
        serverMessage.includes('cannot be deleted');

      showAlert({
        text: isConstraintError
          ? 'Cannot delete activity: it is linked to existing data in use.'
          : serverMessage,
        icon: isConstraintError ? 'warning' : 'error'
      });
    }
  };

  const handleSave = async formData => {
    const htmlMessage = getFormattedMissingFieldsMessage(formData, ['activityName']);
    if (htmlMessage) {
      showAlert({ html: htmlMessage, icon: 'warning' });
      return;
    }

    try {
      const config = {
        method: 'post',
        url: `management/activities/${user.farmId}`,
        data: formData,
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` },
        alertResult: true
      };
      if (state.selectedActivity && state.selectedActivity.activityId) {
        config.method = 'put';
        config.url = `/management/activities/${state.selectedActivity.activityId}`;
      }
      await makeRequest(config);
    } catch (error) {
      console.error('Error saving activity:', error);
      showAlert({ text: error.message, icon: 'error' });
    }
    setState(s => ({ ...s, isAddActivityModalOpen: false, isEditActivityModalOpen: false }));
  };

  const handleAddActivity = () => {
    setState(s => ({
      ...s,
      isAddActivityModalOpen: true,
      selectedActivity: null
    }));
  };

  const filterActivities = state.activities.filter(data =>
    data.activityName.toLowerCase().includes(state.searchTerm)
  );

  const activitiesTableData = {
    columns: [
      {
        key: 'activityName',
        label: t('activities.activityName'),
        cell: (value, _row) => <span>{value ?? _row?.activity_name ?? 'N/A'}</span>
      },
      {
        key: 'isActive',
        label: t('activities.status'),
        cell: (value, _row) => (
          <span className={`activities-status ${value}`}>
            {value ? t('common.active') : t('common.inactive')}
          </span>
        )
      },
      {
        key: 'allDevices',
        label: t('activities.deviceAccess'),
        cell: value => (value ? t('common.yes') : t('common.no'))
      },
      {
        key: 'shared',
        label: t('activities.sharedWithTeam'),
        cell: value => (value ? t('common.yes') : t('common.no'))
      },
      {
        key: 'actions',
        label: t('common.manage'),
        cell: (value, activity) => (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleEdit(activity)}
              title={t('common.edit')}
              className="p-1 rounded transition"
            >
              <PencilSquareIcon className="w-5 h-5 text-blue-500" />
            </button>
            <button
              onClick={() => handleDelete(activity)}
              title={t('common.delete')}
              className="p-1 rounded transition"
            >
              <TrashIcon className="w-5 h-5 text-red-500" />
            </button>
          </div>
        )
      }
    ],
    rows: Array.isArray(filterActivities)
      ? filterActivities.filter(row => row && typeof row === 'object')
      : []
  };

  return (
    <div className="min-h-screen p-6 text-slate-100">
      <SectionIntro
        kicker={t('pageIntro.managementKicker')}
        title={t('nav.activities')}
        description={t('pageIntro.activitiesDescription')}
      />

      <hr className="workspace-summary-divider my-5 border-t" />

      <ActionCardComponent>
        <input
          type="text"
          placeholder={t('activities.searchPlaceholder')}
          value={state.searchTerm}
          onChange={e => setState(s => ({ ...s, searchTerm: e.target.value.toLowerCase() }))}
          className="search-input"
          data-align="left"
        />
        <ButtonComponent onClick={handleAddActivity}>
          <PlusIcon className="w-5 h-5" />
          <span className="font-medium">{t('activities.newActivity')}</span>
        </ButtonComponent>

        <ButtonComponent onClick={handleExportExcel}>
          <DocumentArrowDownIcon className="w-5 h-5" />
          <span className="font-medium">{t('export.excel')}</span>
        </ButtonComponent>

        <ButtonComponent onClick={handleExportPDF}>
          <DocumentArrowDownIcon className="w-5 h-5" />
          <span className="font-medium">{t('export.pdf')}</span>
        </ButtonComponent>
      </ActionCardComponent>

      <TableComponent
        tableData={activitiesTableData}
        defaultSortColumn={activitiesTableData.columns[0]?.key}
      />

      {/* <div className='pagination'>
          <span className='pagination-info'>
            showing {startIndex + 1}-{Math.min(endIndex, filteredActivities.length)} de {filteredActivities.length}
          </span>
          <div className='pagination-controls'>
            <button
              className='pagination-button'
              onClick={handlePageChange}
              disabled={page === 1}
            >
              <ChevronLeftIcon className='w-5 h-5' />
            </button>
            {[...Array(totalPages)].map((_, index) => {
              const pageNumber = index + 1
              if (
                pageNumber === 1 ||
                pageNumber === totalPages ||
                (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
              ) {
                return (
                  <button
                    key={pageNumber}
                    className={`pagination-button ${pageNumber === currentPage ? 'active' : ''}`}
                    onClick={() => handlePageChange(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                )
              }
              if (
                pageNumber === currentPage - 2 ||
                pageNumber === currentPage + 2
              ) {
                return <span key={pageNumber}>...</span>
              }
              return null
            })}
            <button
              className='pagination-button'
              onClick={handlePageChange}
              disabled={currentPage === totalPages}
            >
              <ChevronRightIcon className='w-5 h-5' />
            </button>
          </div>
        </div> */}

      {/* migrate to ModalComponent */}
      {state.isEditActivityModalOpen && (
        <EditActivityModal
          activity={state.selectedActivity}
          onClose={() => setState(s => ({ ...s, isEditActivityModalOpen: false }))}
          onSave={handleSave}
        />
      )}

      {/* migrate to ModalComponent */}
      {state.isAddActivityModalOpen && (
        <AddActivityModal
          onClose={() => setState(s => ({ ...s, isAddActivityModalOpen: false }))}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default ActivitiesManagement;
